/**
 * M06 — raising a job request.
 *
 * The web wizard and the phone create a job through the same endpoint with the
 * same body, so the body is declared here once, against the service that
 * accepts it (backend m06 JobRequestService.CreateJobRequestDto / SaveDraftDto
 * and GeoLocationService.previewDuplicate). The phone used to post an invented
 * shape to `/request/jobs`, a route the server never had.
 */

/** Values t077_job_request_header.area_unit accepts. */
export type JobRequestAreaUnit = 'SQ_FT' | 'SQ_MT' | 'SQ_METER';

/**
 * Units offered for a new job. SQ_METER is the backend's migration alias of
 * SQ_MT, kept so old rows still read; a new job has no reason to choose it.
 */
export const NEW_JOB_AREA_UNITS: readonly JobRequestAreaUnit[] = ['SQ_FT', 'SQ_MT'];

/** The offered unit a legacy code is another spelling of (m06 AreaUnit). */
const AREA_UNIT_ALIAS_OF: Readonly<Record<string, JobRequestAreaUnit>> = { SQ_METER: 'SQ_MT' };

/**
 * The units a job's area picker offers: those for a new job, with the unit the
 * job already holds standing in for the one it is an alias of (a SQ_METER row
 * offers SQ_METER where SQ_MT would be, not both), and appended when it is no
 * spelling of an offered unit. A stored value so reads as itself, once,
 * instead of silently showing as the first option.
 */
export function areaUnitOptions(held: string | null | undefined): readonly string[] {
  if (!held || (NEW_JOB_AREA_UNITS as readonly string[]).includes(held)) return NEW_JOB_AREA_UNITS;
  const aliasOf = AREA_UNIT_ALIAS_OF[held];
  return aliasOf
    ? NEW_JOB_AREA_UNITS.map((u) => (u === aliasOf ? held : u))
    : [...NEW_JOB_AREA_UNITS, held];
}

export const JOB_REQUESTS_PATH = '/v1/job-requests';
export const JOB_REQUEST_DRAFTS_PATH = '/v1/job-requests/drafts';
export const JOB_REQUEST_DUPLICATE_CHECK_PATH = '/v1/job-requests/duplicate-check';

export function jobRequestDraftPath(draftId: string): string {
  return `${JOB_REQUEST_DRAFTS_PATH}/${encodeURIComponent(draftId)}`;
}

/**
 * POST /v1/job-requests (permission M06_JOB_CREATE). Creates the job in DRAFT;
 * submitting it for approval is the separate POST /v1/job-requests/:id/submit.
 * Tenant and raiser come from the signed-in session, never from the body.
 */
export interface CreateJobRequestBody {
  readonly companyId: string;
  /** The dealer; its territory must be the one chosen below. */
  readonly customerId: string;
  readonly customerName: string;
  readonly territoryId: string;
  /** The branding work being asked for, from the work-type master. */
  readonly workTypeId: string;
  /** > 0 and within M06.AREA.MAX_VALUE; height × width when both are measured. */
  readonly areaRequested: number;
  readonly areaUnit: JobRequestAreaUnit;
  readonly height?: number | null;
  readonly width?: number | null;
  /** The site. Required, and must be a real coordinate. */
  readonly latitude: number;
  readonly longitude: number;
  /** Metres. Only for a device fix; checked against M06.GPS.MAX_ACCURACY_METERS. */
  readonly locationAccuracy?: number | null;
  readonly remarks?: string | null;
  /** The saved draft this job was finished from; the server archives it. */
  readonly fromDraftId?: string | null;
  readonly workflowDefinitionId?: string | null;
  /** R14 — the raiser was shown earlier work at this site and chose to go ahead. */
  readonly siteHistoryAcknowledged?: boolean | null;
}

/** The fields of the created job the screens act on (the full T077 row comes back). */
export interface CreatedJobRequest {
  readonly jobRequestId: string;
  readonly jobNumber?: string | null;
  readonly currentStatus?: string;
}

type Nullable<T> = { readonly [K in keyof T]?: T[K] | null };

/**
 * POST /v1/job-requests/drafts, PUT /v1/job-requests/drafts/:draftId.
 * Every column is optional: a draft is whatever the raiser had so far.
 */
export type SaveJobDraftBody = Nullable<Pick<CreateJobRequestBody,
  | 'companyId' | 'customerId' | 'customerName' | 'territoryId' | 'workTypeId'
  | 'areaRequested' | 'areaUnit' | 'height' | 'width'
  | 'latitude' | 'longitude' | 'locationAccuracy' | 'remarks'>> & {
  readonly surveyData?: Record<string, unknown> | null;
};

export interface SavedJobDraft {
  readonly draftId: string;
}

/** POST /v1/job-requests/duplicate-check — TR-03 verdict before the job exists. */
export interface DuplicateCheckPreviewBody {
  readonly customerId: string;
  readonly workTypeId: string;
  readonly latitude: number;
  readonly longitude: number;
}

export interface DuplicateCheckPreview {
  readonly result: 'NO_DUPLICATE' | 'POTENTIAL_DUPLICATE' | 'CONFIRMED_DUPLICATE';
  readonly action: 'ALLOW' | 'WARN';
  readonly conflictingJobIds: readonly string[];
}

/** The pickers the wizard is built from, as the master endpoints return them. */
export interface CompanyOption { readonly id: string; readonly companyName: string }
export interface CustomerOption {
  readonly customerId: string;
  readonly dealerCode: string;
  readonly companyName: string;
  readonly territoryCode: string;
}
export interface TerritoryOption {
  readonly territoryId: string;
  readonly territoryCode: string;
  readonly territoryName: string;
}
export interface WorkTypeOption { readonly workTypeId: string; readonly workTypeName: string }

/**
 * The territory picker's id for a dealer's own territory. The dealer master
 * (t059) holds the territory as a code while the job holds the id, and the
 * server refuses a dealer outside the chosen territory, so the dealer's code is
 * matched against the territory list. Null when the list has no such code.
 */
export function dealerTerritoryId(
  dealer: Pick<CustomerOption, 'territoryCode'>,
  territories: readonly TerritoryOption[],
): string | null {
  return territories.find((tr) => tr.territoryCode === dealer.territoryCode)?.territoryId ?? null;
}

/**
 * The pickers' masters. Version-qualified, because the phone talks to the
 * server directly (`<host>/v1` + path); the web's dev proxy accepts either form.
 */
export const JOB_REQUEST_LOOKUP_PATHS = {
  companies: '/v1/companies',
  customers: '/v1/master/customers?limit=200',
  territories: '/v1/territories?limit=200',
  workTypes: '/v1/master/work-types',
} as const;

export function customerPath(customerId: string): string {
  return `/v1/master/customers/${encodeURIComponent(customerId)}`;
}

/** Lists come back either bare or wrapped in `{ data }` / `{ items }` per module. */
export function listRows<T>(payload: unknown, key: 'data' | 'items'): readonly T[] {
  if (Array.isArray(payload)) return payload as T[];
  const wrapped = (payload ?? {}) as Record<string, unknown>;
  return Array.isArray(wrapped[key]) ? (wrapped[key] as T[]) : [];
}

// ── After creation: reading, submitting, evidencing a job ──────────────────

/** The six `JobRequestStatus` values T077 stores, in lifecycle order. */
export const JOB_REQUEST_STATUSES = [
  'DRAFT',
  'SUBMITTED',
  'FORWARDED_TO_BRANDING',
  'ASSIGNED_TO_VENDOR',
  'COMPLETED',
  'CANCELLED',
] as const;
export type JobRequestStatus = (typeof JOB_REQUEST_STATUSES)[number];

export function jobRequestPath(jobRequestId: string): string {
  return `${JOB_REQUESTS_PATH}/${encodeURIComponent(jobRequestId)}`;
}
export function jobRequestHistoryPath(jobRequestId: string): string {
  return `${jobRequestPath(jobRequestId)}/history`;
}
/** POST, body `{ workflowDefinitionId?, remarks? }`; the workflow is resolved server-side. */
export function jobRequestSubmitPath(jobRequestId: string): string {
  return `${jobRequestPath(jobRequestId)}/submit`;
}
/** POST, body {@link CancelJobRequestBody}. */
export function jobRequestCancelPath(jobRequestId: string): string {
  return `${jobRequestPath(jobRequestId)}/cancel`;
}
/** GET lists, POST uploads ({@link UploadJobPhotoBody}). */
export function jobRequestPhotosPath(jobRequestId: string): string {
  return `${jobRequestPath(jobRequestId)}/photos`;
}

/** GET /v1/job-requests — the server's real filters; `limit` is capped at 100. */
export interface JobRequestSearch {
  readonly status?: JobRequestStatus;
  readonly customerId?: string;
  readonly territoryId?: string;
  readonly companyId?: string;
  /** YYYY-MM-DD, on request_date. */
  readonly dateFrom?: string;
  readonly dateTo?: string;
  readonly requestedBy?: string;
  readonly page?: number;
  readonly limit?: number;
}

export function jobRequestSearchPath(search: JobRequestSearch = {}): string {
  const qs = Object.entries(search)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return qs ? `${JOB_REQUESTS_PATH}?${qs}` : JOB_REQUESTS_PATH;
}

/** One T077 row; the list adds `waitingOn`, the detail adds `brandingRequestNo`. */
export interface JobRequestHeader {
  readonly jobRequestId: string;
  readonly jobNumber: string | null;
  readonly currentStatus: JobRequestStatus | string;
  readonly companyId?: string;
  readonly customerId: string;
  readonly customerName: string | null;
  readonly dealerCode?: string | null;
  readonly territoryId: string | null;
  readonly workTypeId: string | null;
  /** Numeric columns arrive as strings from Postgres. */
  readonly areaRequested: string | number;
  readonly areaUnit: string;
  readonly height?: string | number | null;
  readonly width?: string | number | null;
  readonly latitude: string | number | null;
  readonly longitude: string | number | null;
  readonly locationAccuracy?: string | number | null;
  readonly requestDate: string | null;
  readonly remarks?: string | null;
  readonly workflowInstanceId?: string | null;
  /** The stage it is sitting at, named as the person holding it (list only). */
  readonly waitingOn?: string | null;
  /** The branding request this job raised, null until it reached the queue (detail only). */
  readonly brandingRequestNo?: string | null;
}

export interface JobRequestPage {
  readonly data: readonly JobRequestHeader[];
  readonly total: number;
}

/** T085 — one status transition, actor joined in by name. */
export interface JobRequestStatusTransition {
  readonly historyId: string;
  readonly fromStatus: string | null;
  readonly toStatus: string;
  readonly transitionReason: string | null;
  readonly transitionedAt: string;
  readonly transitionedBy: string;
  readonly transitionedByName?: string | null;
  readonly transitionedByCode?: string | null;
}

export interface CancelJobRequestBody {
  readonly reasonId: string;
  readonly reasonText: string;
  readonly additionalNotes: string | null;
}

/**
 * The cancellation reasons: the rejection-reason master's M06 rows plus its
 * global (module-less) ones. Unscoped, the master returns every module's
 * reasons — vendor, billing, quality — none of which say why a job was cancelled.
 */
export const CANCEL_REASONS_PATH = '/v1/master/rejection-reasons?status=ACTIVE&module=M06';

/** `PhotoType` (backend m06.enums.ts), letter for letter. */
export const JOB_PHOTO_TYPES = ['FRONT_VIEW', 'SIDE_VIEW', 'WIDE_VIEW', 'LOCATION_CONTEXT', 'OTHER'] as const;
export type JobPhotoType = (typeof JOB_PHOTO_TYPES)[number];

/**
 * POST /v1/job-requests/:id/photos (M06_PHOTO_UPLOAD). The server refuses a
 * photo without GPS or a timestamp, one over M06.PHOTO.MAX_SIZE_KB, a type
 * other than JPEG/PNG/WebP (EX-JR-06), and the same bytes twice (EX-JR-07).
 * Submitting needs at least M06.PHOTO.MIN_COUNT of them (EX-JR-05).
 */
export interface UploadJobPhotoBody {
  readonly photoType: JobPhotoType;
  readonly latitude: number | null;
  readonly longitude: number | null;
  /** ISO time the photo was taken. */
  readonly capturedAt: string;
  readonly deviceId?: string | null;
  readonly fileName: string;
  /** SHA-256 hex of the image bytes. */
  readonly fileHash: string;
  readonly fileSizeKb: number;
  readonly mimeType: string;
  /** The image itself; the server stores it and decides the path. */
  readonly imageBase64: string;
  readonly hasGpsEmbedded: boolean;
  readonly hasTimestampEmbedded: boolean;
}

/** T079 — a photo the server holds. */
export interface JobRequestPhoto {
  readonly photoId: string;
  readonly photoType: string;
  readonly latitude: string | number | null;
  readonly longitude: string | number | null;
  readonly capturedAt: string;
  readonly fileHash: string;
  readonly fileName: string;
}
